/**
 * Select-to-ask (issue #56): build the structured chat message sent when a
 * reviewer selects lines in the code viewer and clicks "Ask Claude".
 *
 * The message embeds the file path, the 1-based line range, and the selected
 * text, then ends with a default question. The chat agent's read_file /
 * search_code tools let Claude pull surrounding context, so the embedded
 * snippet is capped — a whole-file selection would only bloat the prompt.
 */

/** A non-empty, normalized Monaco selection, as reported by CodeViewer. */
export type AskSelection = {
  startLine: number;
  endLine: number;
  text: string;
};

/** A selection plus the file it came from — buildAskMessage's input. */
export type AskTarget = AskSelection & { path: string };

/** An AskTarget routed through Reviewer state; `nonce` distinguishes
 *  repeat asks on the same lines from a re-render of the same request. */
export type AskClaudeRequest = AskTarget & { nonce: number };

/** Embed at most this many lines of the selection. */
export const ASK_MAX_LINES = 100;
/** ...and at most this many characters. */
export const ASK_MAX_CHARS = 4096;

/** Length of the longest backtick run inside `s` (0 if none). */
function backtickRun(s: string): number {
  let max = 0;
  for (const m of s.matchAll(/`+/g)) max = Math.max(max, m[0].length);
  return max;
}

/** Wrap `path` in an inline code span whose delimiter is longer than any
 *  backtick run inside it (CommonMark rules), so an exotic filename can't
 *  break the message structure. */
function inlinePathSpan(path: string): string {
  const run = backtickRun(path);
  if (run === 0) return `\`${path}\``;
  const ticks = "`".repeat(run + 1);
  return `${ticks} ${path} ${ticks}`;
}

export function buildAskMessage(req: AskTarget): string {
  let snippet = req.text;
  let truncated = false;

  const lines = snippet.split("\n");
  if (lines.length > ASK_MAX_LINES) {
    snippet = lines.slice(0, ASK_MAX_LINES).join("\n");
    truncated = true;
  }
  if (snippet.length > ASK_MAX_CHARS) {
    snippet = snippet.slice(0, ASK_MAX_CHARS);
    // Don't leave a lone high surrogate if the cut split an astral char —
    // the resulting invalid UTF-8 would be rejected by the API.
    if (/[\uD800-\uDBFF]$/.test(snippet)) snippet = snippet.slice(0, -1);
    truncated = true;
  }

  // The fence must out-run any backtick run inside the snippet (a code file
  // can legitimately contain ``` or even ```` in docs/heredocs).
  const fence = "`".repeat(Math.max(4, backtickRun(snippet) + 1));

  const marker = truncated ? "\n…(selection truncated)" : "";
  return (
    `Regarding ${inlinePathSpan(req.path)} lines ${req.startLine}–${req.endLine}:\n\n` +
    `${fence}\n${snippet}\n${fence}${marker}\n\n` +
    `Explain what this does.`
  );
}
